// Client Portal-specific AG Grid hook
// Extends the base AgGrid hook with client portal-specific cell renderers

import { AgGrid } from "./ag_grid_hook"
import {
  TourFeedbackCellRenderer,
  PropertyActionsCellRenderer,
  SubleaseCheckCellRenderer,
  LocationCellRenderer,
} from "./client_portal_ag_grid_renderers"
import { themeQuartz } from "ag-grid-community"

// Create custom theme with Client Portal colors.
// Backgrounds stay transparent so the grid sits on the surrounding Panel's
// surface instead of painting its own white band.
const clientPortalTheme = themeQuartz.withParams({
  backgroundColor: "transparent",
  headerBackgroundColor: "transparent",
  rowBackgroundColor: "transparent",
  oddRowBackgroundColor: "transparent",
})

// Override getCustomComponents to provide client portal renderers
export const ClientPortalAgGrid = {
  ...AgGrid,

  // Store parent initGrid reference
  _parentInitGrid: AgGrid.initGrid,

  // Override initGrid to use Quartz theme and inject pushEvent into cellRendererParams
  initGrid() {
    // Override gridOptions to use Quartz theme before parent initialization
    const originalGridOptions = this.gridOptions

    // Call parent to set up and create the grid
    this._parentInitGrid.call(this)

    // After grid is created, update column defs to inject pushEvent and disable sidebar
    if (this.api) {
      const columnDefs = this.api.getColumnDefs()
      const updatedColumnDefs = columnDefs.map((col, idx) => {
        // Remove the bold font-weight from the first column (applied by base AgGrid hook)
        if (idx === 0 && col.cellStyle) {
          delete col.cellStyle
        }

        // Add header indent to Location column to align with dot spacer in cells
        if (col.field === "building_address") {
          col.headerClass = "ag-header-cell-indent-dot"
        }

        if (
          col.cellRenderer === "TourFeedbackCellRenderer" ||
          col.cellRenderer === "PropertyActionsCellRenderer"
        ) {
          return {
            ...col,
            cellRendererParams: {
              ...col.cellRendererParams,
              pushEvent: (event, params) => this.pushEvent(event, params),
            },
          }
        }

        // Handle expires_at field: use raw ISO date for sorting, formatted date for display
        if (col.field === "expires_at") {
          return {
            ...col,
            valueGetter: (params) => params.data?.expires_at_raw,
            valueFormatter: (params) => params.data?.expires_at || "",
            comparator: (valueA, valueB) => {
              if (!valueA) return 1
              if (!valueB) return -1
              return valueA.localeCompare(valueB)
            },
          }
        }

        return col
      })

      this.api.updateGridOptions({
        columnDefs: updatedColumnDefs,
        sideBar: null, // Completely disable the sidebar (columns toggle)
        theme: clientPortalTheme, // Use customized Quartz theme with Client Portal colors
        domLayout: "normal", // Fixed height with scrolling
        rowHeight: 56, // Taller rows for client portal
        onRowClicked: null, // Disable base hook's row click handler (we add our own below)
      })

      // Explicitly hide the sidebar using the API (belt and suspenders approach)
      this.api.setSideBarVisible(false)

      // Add row click handler to open details modal
      this.api.addEventListener("rowClicked", (params) => {
        // Use considered_place_id for opening the details modal
        if (params.data?.considered_place_id) {
          this.pushEvent("show_details", {
            id: params.data.considered_place_id,
          })
        }
      })

      // Add hover event handlers for map cross-highlighting
      this.api.addEventListener("cellMouseOver", (params) => {
        if (params.data?.considered_place_id) {
          window.dispatchEvent(
            new CustomEvent("survey-map:highlight-property", {
              detail: { propertyId: params.data.considered_place_id },
            }),
          )
        }
      })

      this.api.addEventListener("cellMouseOut", (params) => {
        if (params.data?.considered_place_id) {
          window.dispatchEvent(
            new CustomEvent("survey-map:unhighlight-property", {
              detail: { propertyId: params.data.considered_place_id },
            }),
          )
        }
      })

      // Listen for map marker hover events to highlight grid rows
      this.setupGridHighlightListeners()
    }
  },

  // Set up listeners for map -> grid cross-highlighting
  setupGridHighlightListeners() {
    const hook = this

    this.gridHighlightHandler = (event) => {
      const propertyId = event.detail.propertyId
      hook.api.forEachNode((node) => {
        if (node.data?.considered_place_id === propertyId) {
          const rowElement = document.querySelector(`[row-id="${node.id}"]`)
          if (rowElement) {
            rowElement.classList.add("ag-row-highlighted")
          }
        }
      })
    }

    this.gridUnhighlightHandler = (event) => {
      const propertyId = event.detail.propertyId
      hook.api.forEachNode((node) => {
        if (node.data?.considered_place_id === propertyId) {
          const rowElement = document.querySelector(`[row-id="${node.id}"]`)
          if (rowElement) {
            rowElement.classList.remove("ag-row-highlighted")
          }
        }
      })
    }

    window.addEventListener(
      "survey-grid:highlight-row",
      this.gridHighlightHandler,
    )
    window.addEventListener(
      "survey-grid:unhighlight-row",
      this.gridUnhighlightHandler,
    )
  },

  getCustomComponents() {
    return {
      TourFeedbackCellRenderer: TourFeedbackCellRenderer,
      PropertyActionsCellRenderer: PropertyActionsCellRenderer,
      SubleaseCheckCellRenderer: SubleaseCheckCellRenderer,
      LocationCellRenderer: LocationCellRenderer,
    }
  },
}
